import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as authApi from '../api/auth';
import type { AuthUser } from '../api/types';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('vproxy_token'));
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 处理 OIDC 回调（从 URL query 中获取 token）
    const params = new URLSearchParams(location.search);
    const callbackToken = params.get('token');
    const callbackUser = params.get('user');

    if (callbackToken && callbackUser) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(callbackUser));
        localStorage.setItem('vproxy_token', callbackToken);
        localStorage.setItem('vproxy_user', JSON.stringify(parsedUser));
        setToken(callbackToken);
        setUser(parsedUser);
        navigate('/dashboard', { replace: true });
        setIsLoading(false);
        return;
      } catch {
        // 解析失败，清除 URL 参数
        navigate(location.pathname, { replace: true });
      }
    }

    // 从 localStorage 恢复
    const storedToken = localStorage.getItem('vproxy_token');
    const storedUser = localStorage.getItem('vproxy_user');

    if (storedToken && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        // 验证 token 有效性
        authApi.getCurrentUser().then((res) => {
          if (res.code === 0) {
            setUser(res.data);
            localStorage.setItem('vproxy_user', JSON.stringify(res.data));
          } else {
            handleAuthFailure();
          }
        }).catch(() => handleAuthFailure());
      } catch {
        handleAuthFailure();
      }
    }
    setIsLoading(false);

    function handleAuthFailure() {
      localStorage.removeItem('vproxy_token');
      localStorage.removeItem('vproxy_user');
      setToken(null);
      setUser(null);
    }
  }, []);

  const login = useCallback(async () => {
    try {
      const res = await authApi.getLoginUrl();
      if (res.code === 0) {
        window.location.href = res.data.authorizeUrl;
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    localStorage.removeItem('vproxy_token');
    localStorage.removeItem('vproxy_user');
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
